import React, { useState, useEffect } from 'react'
import { Table, Button, Input, Upload, Tag, Space, Tooltip, Spin, Modal, App } from 'antd'
import { AppCard } from '@/shared/components/ui'
import { PlusOutlined, SearchOutlined, DeleteOutlined, UploadOutlined, EyeOutlined, ExperimentOutlined, FileTextOutlined } from '@ant-design/icons'
import { useI18n } from '@/shared/i18n'
import { useKnowledgeStore, type KnowledgeItem } from './KnowledgeBase.store'
import { useUploadDocument, useDeleteDocument } from '@/shared/hooks/useKnowledgeQueries'
import { useDocumentWebSocket } from '@/shared/hooks/useDocumentWebSocket'
import { useCurrentUser } from '@/stores/authStore'
import DocumentChunksViewer from './DocumentChunksViewer'
import ExtractionTest from './ExtractionTest'
import './KnowledgeBase.scss'

const KnowledgeBase: React.FC = () => {
  const { message } = App.useApp()
  const { t } = useI18n('modules')
  const {
    filteredItems,
    isLoading,
    error,
    fetchItems,
    searchItems,
    deleteItem,
    addItem,
    updateItem
  } = useKnowledgeStore()
  const [searchText, setSearchText] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [viewMode, setViewMode] = useState<'list' | 'detail' | 'extraction-test'>('list')
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)

  // 获取当前用户和公司ID
  const user = useCurrentUser()
  const companyId = user?.company_id || 0

  // WebSocket文档状态订阅
  const {
    documentStatuses,
    isConnected,
    subscribeToDocuments,
    unsubscribeFromDocuments,
    getDocumentStatus
  } = useDocumentWebSocket({
    companyId,
    onStatusUpdate: (update) => {
      // 当文档状态更新时，刷新文档列表以获取最新状态
      if (update.status === 'completed' || update.status === 'failed') {
        fetchItems()
      }
    },
    autoConnect: true,
  })

  const uploadDocumentMutation = useUploadDocument()
  const deleteDocumentMutation = useDeleteDocument()

  // 使用真实数据从store
  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  // 显示错误信息
  useEffect(() => {
    if (error) {
      console.error('知识库错误:', error)
      message.error(error)
    }
  }, [error, message])

  const handleSearch = (value: string) => {
    setSearchText(value)
    searchItems(value)
  }

  const handleAdd = () => {
    message.info(t('knowledge.messages.uploadInstructions'))
    // 注释掉原有的模态框打开逻辑
    // setSelectedItem(null)
    // form.resetFields()
    // setIsModalVisible(true)
  }


  const handleDelete = (id: string) => {
    Modal.confirm({
      title: t('knowledge.messages.deleteConfirmTitle'),
      content: t('knowledge.messages.deleteConfirmContent'),
      onOk: () => {
        deleteDocumentMutation.mutate(id, {
          onSuccess: () => {
            // 状态失效逻辑已在useDeleteDocument hook中处理
            // 刷新文档列表以更新store状态
            fetchItems()
          },
          onError: (error) => {
            console.error('删除文档失败:', error)
            message.error(t('knowledge.messages.deleteFailed'))
          }
        })
      },
    })
  }

  const handleUpload = async (file: File) => {
    // 验证文件类型
    const allowedExtensions = ['.pdf', '.txt', '.md', '.docx']
    const fileExtension = file.name.toLowerCase().slice((file.name.lastIndexOf('.') - 1 >>> 0) + 2)
    const isValidExtension = allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext))

    if (!isValidExtension) {
      message.error(t('knowledge.messages.unsupportedFileType', {
        extension: `.${fileExtension}`,
        allowedExtensions: allowedExtensions.join(', ')
      }))
      return
    }

    // 验证文件大小（可选，例如限制为100MB）
    const maxSize = 100 * 1024 * 1024 // 100MB
    if (file.size > maxSize) {
      message.error(t('knowledge.messages.fileSizeExceeded', {
        size: (file.size / (1024 * 1024)).toFixed(2)
      }))
      return
    }

    try {
      setUploading(true)
      setUploadProgress(0)

      const onProgress = (progress: number) => {
        setUploadProgress(progress)
      }

      const response = await uploadDocumentMutation.mutateAsync({
        file,
        onProgress
      })

      message.success(t('knowledge.messages.uploadSuccess'))
      setUploading(false)
      setUploadProgress(0)

      // 订阅文档状态更新
      if (response?.document_id) {
        subscribeToDocuments([response.document_id])
        console.log(`已订阅文档状态更新: ${response.document_id}`)
      } else {
        console.warn('上传响应中未找到document_id，无法订阅状态更新')
      }

      // 刷新文档列表
      fetchItems()
    } catch (error) {
      console.error('文件上传失败:', error)
      message.error(t('knowledge.messages.uploadFailed'))
      setUploading(false)
      setUploadProgress(0)
    }
  }

  // 处理查看文档详情
  const handleViewDetail = (documentId: string) => {
    setSelectedDocumentId(documentId)
    setViewMode('detail')
  }

  // 处理返回列表
  const handleBackToList = () => {
    setViewMode('list')
    setSelectedDocumentId(null)
  }

  // 处理切换到抽取测试
  const handleSwitchToExtractionTest = () => {
    setViewMode('extraction-test')
  }

  // 处理切换到文档列表
  const handleSwitchToList = () => {
    setViewMode('list')
    setSelectedDocumentId(null)
  }


  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // 状态标签颜色
  const getStatusColor = (status: string): string => {
    const statusMap: Record<string, string> = {
      pending: 'blue',
      parsing: 'cyan',
      semantic_processing: 'geekblue',
      embedding: 'purple',
      persisting: 'orange',
      completed: 'green',
      failed: 'red',
      active: 'green',
      unknown: 'default'
    }
    return statusMap[status.toLowerCase()] || 'default'
  }

  const columns = [
    {
      title: t('knowledge.table.columns.fileName'),
      dataIndex: 'file_name',
      key: 'file_name',
      width: 300,
      ellipsis: true,
      render: (text: string, record: KnowledgeItem) => (
        <Tooltip title={`${text}${record.document_description ? '\n' + t('knowledge.table.columns.description') + record.document_description : ''}`}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: t('knowledge.table.columns.fileType'),
      dataIndex: 'file_type',
      key: 'file_type',
      width: 100,
      render: (fileType: string) => (
        <Tag color="blue">{fileType ? fileType.toUpperCase() : t('knowledge.fileTypes.unknown')}</Tag>
      ),
    },
    {
      title: t('knowledge.table.columns.fileSize'),
      dataIndex: 'file_size',
      key: 'file_size',
      width: 100,
      render: (size: number) => formatFileSize(size),
    },
    {
      title: t('knowledge.table.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string, record: KnowledgeItem) => {
        // 优先使用WebSocket实时状态
        const realtimeStatus = getDocumentStatus(record.id);
        const displayStatus = realtimeStatus?.status || status;
        const displayProgress = realtimeStatus?.progress;

        const statusTextMap: Record<string, string> = {
          pending: t('knowledge.status.pending'),
          parsing: t('knowledge.status.parsing'),
          semantic_processing: t('knowledge.status.semantic_processing'),
          embedding: t('knowledge.status.embedding'),
          persisting: t('knowledge.status.persisting'),
          completed: t('knowledge.status.completed'),
          failed: t('knowledge.status.failed'),
          active: t('knowledge.status.active'),
          unknown: t('knowledge.status.unknown')
        }

        const statusText = statusTextMap[displayStatus] || displayStatus;
        const statusColor = getStatusColor(displayStatus);

        // 如果正在处理中，显示进度
        if (displayProgress !== undefined && displayProgress > 0 && displayProgress < 100) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Tag color={statusColor}>
                {statusText}
              </Tag>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {displayProgress}%
              </span>
            </div>
          );
        }

        return (
          <Tag color={statusColor}>
            {statusText}
          </Tag>
        );
      },
    },
    {
      title: t('knowledge.table.columns.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => {
        try {
          const d = new Date(date)
          return d.toLocaleDateString(t('knowledge.dateFormat.locale')) + ' ' + d.toLocaleTimeString(t('knowledge.dateFormat.locale'), { hour: '2-digit', minute: '2-digit' })
        } catch {
          return date
        }
      },
    },
    {
      title: t('knowledge.table.columns.chunks'),
      dataIndex: 'total_chunks',
      key: 'total_chunks',
      width: 80,
      render: (count: number) => (
        <Tag color={count > 0 ? 'green' : 'orange'}>
          {count}
        </Tag>
      ),
    },
    {
      title: t('knowledge.table.columns.actions'),
      key: 'actions',
      width: 120,
      render: (_: any, record: KnowledgeItem) => (
        <Space>
          <Tooltip title={t('knowledge.buttons.viewDetails')}>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record.id)}
            />
          </Tooltip>
          <Tooltip title={t('knowledge.buttons.deleteDocument')}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  // 使用store中的filteredItems作为表格数据源
  const tableData = filteredItems

  return (
    <div className="knowledge-base">
      <div className="knowledge-header">
        <AppCard>
          <div className="knowledge-toolbar">
            <Space.Compact className="search-input">
              <Input
                placeholder={t('knowledge.search.placeholder')}
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={() => handleSearch(searchText)}
                className="search-input-field"
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => handleSearch(searchText)}
              />
            </Space.Compact>
            <Space>
              <Button
                type={viewMode === 'list' ? 'primary' : 'default'}
                icon={<FileTextOutlined />}
                onClick={handleSwitchToList}
              >
                {t('knowledge.buttons.documentList')}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                {t('knowledge.buttons.addKnowledge')}
              </Button>
              <Upload
                showUploadList={false}
                beforeUpload={(file) => {
                  handleUpload(file as File)
                  return false // 阻止默认上传，使用自定义处理
                }}
                disabled={uploading}
              >
                <Button
                  icon={uploading ? <Spin size="small" /> : <UploadOutlined />}
                  disabled={uploading}
                >
                  {uploading ? t('knowledge.buttons.uploading', { progress: uploadProgress }) : t('knowledge.buttons.importFile')}
                </Button>
              </Upload>
              <Button
                type={viewMode === 'extraction-test' ? 'primary' : 'default'}
                icon={<ExperimentOutlined />}
                onClick={handleSwitchToExtractionTest}
              >
                {t('knowledge.buttons.extractionTest')}
              </Button>
            </Space>
          </div>
        </AppCard>
      </div>

      {/* 根据视图模式渲染不同内容 */}
      {viewMode === 'list' ? (
        <AppCard className="knowledge-content">
          <Table
            dataSource={tableData}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            size="middle"
            loading={isLoading}
            locale={{ emptyText: isLoading ? t('knowledge.table.loadingText') : t('knowledge.table.emptyText') }}
            scroll={{ x: 'max-content' }}
          />
        </AppCard>
      ) : viewMode === 'detail' ? (
        <AppCard className="knowledge-content">
          {selectedDocumentId && (
            <DocumentChunksViewer
              documentId={selectedDocumentId}
              onBackToList={handleBackToList}
            />
          )}
        </AppCard>
      ) : (
        <AppCard className="knowledge-content">
          <ExtractionTest />
        </AppCard>
      )}

    </div>
  )
}

export default KnowledgeBase